import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';

import { isAxiosError } from 'axios';

import type { ServiceRequestConfig } from '~/services/createService';

import { JsonApiError } from '~/services/errors/JsonApiError';
import { atlas } from '~/services/serviceInstances';

export type AtlasBaseQueryArg = {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  rtkQueryOptions?: ServiceRequestConfig;
};

// The atlas axios instance already targets `${Configuration.atlas}/api/v1`,
// while the generated paths carry the prefix from the OpenAPI schema.
const normalizeUrl = (rawUrl: string) => {
  const withoutApiPrefix = rawUrl.replace(/^\/?api\/v1/, '');
  return withoutApiPrefix.startsWith('/')
    ? withoutApiPrefix
    : `/${withoutApiPrefix}`;
};

export const atlasBaseQuery: BaseQueryFn<
  AtlasBaseQueryArg,
  unknown,
  unknown
> = async ({ url, method = 'get', body, params, rtkQueryOptions }) => {
  const path = normalizeUrl(url);
  const config: ServiceRequestConfig = {
    showErrorToast: true,
    ...rtkQueryOptions,
    params,
    // indexes: null → repeat format (key=v1&key=v2) instead of Axios default
    // bracket format (key[]=v1), which FastAPI List params require.
    paramsSerializer: { indexes: null },
  };

  try {
    let result: unknown;
    const m = String(method).toLowerCase();
    if (m === 'get') {
      result = await atlas.get(path, config);
    } else if (m === 'post') {
      result = await atlas.post(path, body, config);
    } else if (m === 'patch') {
      result = await atlas.patch(path, body, config);
    } else if (m === 'put') {
      result = await atlas.put(path, body, config);
    } else if (m === 'delete') {
      result = await atlas.remove(path, { ...config, data: body });
    } else {
      return { error: { status: 400, data: `Unsupported method: ${method}` } };
    }
    return { data: result };
  } catch (e) {
    // atlas overrides createService's error handler, so failures surface as
    // JsonApiError rather than AxiosError.
    if (e instanceof JsonApiError) {
      const status = Number.parseInt(e.errors[0]?.status ?? '', 10);
      return {
        error: {
          status: Number.isNaN(status) ? 'FETCH_ERROR' : status,
          data: { errors: e.errors },
        },
      };
    }
    if (isAxiosError(e)) {
      return {
        error: {
          status: e.response?.status ?? 'FETCH_ERROR',
          data: e.response?.data ?? e.message,
        },
      };
    }
    return { error: { status: 'FETCH_ERROR', error: String(e) } };
  }
};
